import { PrivateImage } from "@/components/PrivateMedia";
import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldOff, User } from "lucide-react";
import { useI18n } from "@/contexts/LanguageContext";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function BlockedUsers() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get("/blocks");
      setItems(r.data.items || []);
    } catch (e) {
      setError(extractError(e, t("app.moderation.loadError")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUnblock = async () => {
    if (!confirmTarget) return;
    setBusy(true);
    try {
      await api.delete(`/blocks/${confirmTarget.target_user_id}`);
      toast.success(t("app.moderation.unblockDone"));
      setConfirmTarget(null);
      await load();
    } catch (e) {
      toast.error(extractError(e, t("app.moderation.loadError")));
    } finally {
      setBusy(false);
    }
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", {
        year: "numeric", month: "long", day: "numeric",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="max-w-3xl" data-testid="blocked-users-page">
      <button
        onClick={() => navigate("/app/settings")}
        className="flex items-center gap-2 text-sm mb-4"
        style={{ color: "hsl(var(--nvl-ink-soft))" }}
        data-testid="blocked-users-back"
      >
        <ArrowLeft size={14} /> {t("app.nav.settings")}
      </button>
      <p className="nvl-overline" style={{ color: "hsl(var(--nvl-accent))" }}>
        {t("app.moderation.overflowMenu")}
      </p>
      <h1 className="mt-3 text-4xl md:text-5xl">{t("app.moderation.blockedListTitle")}</h1>
      <p className="mt-3 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
        {t("app.moderation.blockedListSubtitle")}
      </p>

      <div className="mt-8 nvl-card p-4 md:p-6">
        {loading ? (
          <div className="p-4 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}
               data-testid="blocked-users-loading">
            {t("app.moderation.loading")}
          </div>
        ) : error ? (
          <div className="p-4 text-sm" style={{ color: "hsl(var(--destructive))" }}
               data-testid="blocked-users-error">
            {error}
          </div>
        ) : items.length === 0 ? (
          <div className="p-6 text-center" data-testid="blocked-users-empty">
            <ShieldOff size={24} className="mx-auto mb-3"
                       style={{ color: "hsl(var(--nvl-ink-soft))" }} />
            <p className="text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("app.moderation.blockedListEmpty")}
            </p>
          </div>
        ) : (
          <ul className="divide-y" style={{ borderColor: "hsl(var(--nvl-border))" }}
              data-testid="blocked-users-list">
            {items.map((it) => (
              <li key={it.target_user_id}
                  className="py-4 flex items-center gap-4"
                  data-testid={`blocked-user-item-${it.target_user_id}`}>
                <div
                  className="w-12 h-12 rounded-full overflow-hidden flex items-center justify-center"
                  style={{ backgroundColor: "hsl(var(--muted))" }}
                >
                  {it.profile?.main_photo_url ? (
                    <PrivateImage
                      src={it.profile.main_photo_url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User size={18} style={{ color: "hsl(var(--nvl-ink-soft))" }} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {it.profile?.display_name || "Member"}
                  </div>
                  <div className="text-xs" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                    {t("app.moderation.blockedSince", { date: fmtDate(it.created_at) })}
                  </div>
                </div>
                <button
                  onClick={() => setConfirmTarget(it)}
                  data-testid={`blocked-user-unblock-${it.target_user_id}`}
                  className="nvl-btn nvl-btn-outline text-sm"
                >
                  {t("app.moderation.unblock")}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <AlertDialog
        open={!!confirmTarget}
        onOpenChange={(open) => !open && setConfirmTarget(null)}
      >
        <AlertDialogContent data-testid="unblock-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("app.moderation.unblockConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("app.moderation.unblockConfirmBody")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="unblock-confirm-cancel">
              {t("app.moderation.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUnblock}
              disabled={busy}
              data-testid="unblock-confirm-cta"
            >
              {t("app.moderation.unblockConfirmCta")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
